import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useAuthStore, useNotificationStore, useUIStore } from '../../stores';
import { Avatar, Dropdown } from '../ui';
import { changeLanguage } from '../../i18n';
import { getEnabledLanguages } from '../../config/languages';
import { searchService } from '../../services';

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { unreadCount } = useNotificationStore();
  const { toggleMobileMenu } = useUIStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
  };

  const handleLogout = async () => {
    await logout();
  };

  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await searchService.globalSearch(query.trim(), 5);
      setSearchResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 300);
    } else {
      setSearchResults(null);
      setShowResults(false);
    }
  };

  const handleResultClick = (type, id) => {
    setSearchQuery('');
    setSearchResults(null);
    setShowResults(false);

    if (type === 'project') {
      navigate(`/projects/${id}`);
    } else if (type === 'task') {
      navigate(`/tasks/${id}`);
    } else if (type === 'client') {
      navigate(`/clients/${id}`);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setShowResults(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close results on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showResults) {
        setShowResults(false);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showResults]);

  const userMenuItems = [
    { label: t('nav.profile'), onClick: () => window.location.href = '/settings/profile' },
    { label: t('nav.settings'), onClick: () => window.location.href = '/settings' },
    { divider: true },
    { label: t('nav.logout'), onClick: handleLogout, danger: true }
  ];

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          {/* <button
            onClick={toggleSidebar}
            className="hidden lg:block p-2 rounded-md hover:bg-gray-100"
          >
            <Bars3Icon className="w-6 h-6" />
          </button> */}

          <div className="hidden sm:block relative w-64">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults) {
                    setShowResults(true);
                  }
                }}
                placeholder={t('common.search') || 'Search...'}
                className="pl-10 pr-10 py-2 w-full text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchResults && (
              <div
                ref={resultsRef}
                className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50"
              >
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {t('common.searching') || 'Searching...'}
                  </div>
                ) : (
                  <>
                    {/* Projects */}
                    {searchResults.projects && searchResults.projects.length > 0 && (
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          {t('projects.title') || 'Projects'}
                        </div>
                        {searchResults.projects.map((project) => (
                          <button
                            key={project._id}
                            onClick={() => handleResultClick('project', project._id)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 group"
                          >
                            <FolderIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {project.name}
                              </div>
                              {project.description && (
                                <div className="text-xs text-gray-500 truncate">
                                  {project.description}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Tasks */}
                    {searchResults.tasks && searchResults.tasks.length > 0 && (
                      <div className="p-2 border-t border-gray-100">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          {t('tasks.title') || 'Tasks'}
                        </div>
                        {searchResults.tasks.map((task) => (
                          <button
                            key={task._id}
                            onClick={() => handleResultClick('task', task._id)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 group"
                          >
                            <ClipboardDocumentListIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {task.title}
                              </div>
                              {task.project && (
                                <div className="text-xs text-gray-500 truncate">
                                  {t('projects.title')}: {task.project.name}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Clients */}
                    {searchResults.clients && searchResults.clients.length > 0 && (
                      <div className="p-2 border-t border-gray-100">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          {t('clients.title') || 'Clients'}
                        </div>
                        {searchResults.clients.map((client) => (
                          <button
                            key={client._id}
                            onClick={() => handleResultClick('client', client._id)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 group"
                          >
                            <BuildingOfficeIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">
                                {client.name}
                              </div>
                              {client.company && (
                                <div className="text-xs text-gray-500 truncate">
                                  {client.company}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No Results */}
                    {(!searchResults.projects || searchResults.projects.length === 0) &&
                      (!searchResults.tasks || searchResults.tasks.length === 0) &&
                      (!searchResults.clients || searchResults.clients.length === 0) && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {t('common.noResults') || 'No results found'}
                        </div>
                      )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <Menu as="div" className="relative">
            <Menu.Button className="p-2 rounded-md hover:bg-gray-100">
              <GlobeAltIcon className="w-5 h-5 text-gray-600" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
                {getEnabledLanguages().map((lang) => (
                  <Menu.Item key={lang.code}>
                    {({ active }) => (
                      <button
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full px-4 py-2 text-sm text-left rtl:text-right flex items-center gap-2 ${active ? 'bg-gray-50' : ''
                          } ${i18n.language === lang.code ? 'text-primary font-medium' : ''}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.nativeName}</span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Notifications */}
          <Link to="/notifications" className="relative p-2 rounded-md hover:bg-gray-100">
            <BellIcon className="w-5 h-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 text-xs font-medium text-white bg-danger rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>

          {/* User Menu */}
          <Dropdown
            trigger={
              <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100">
                <Avatar
                  src={user?.avatar ? (user.avatar.startsWith('http') ? user.avatar : process.env.REACT_APP_SOCKET_URL + user.avatar) : null}
                  name={user ? `${user.firstName} ${user.lastName}` : ''}
                  size="sm"
                />
                <span className="hidden sm:block text-sm font-medium text-gray-700">
                  {user?.firstName}
                </span>
              </button>
            }
            items={userMenuItems}
          />
        </div>
      </div>
    </header>
  );
}
